/** The partner catalog (platform admins): the checked-in entries (app/design/partners/catalog.yaml) with the edits
 * made here on top. An entry is edited as JSON (validated by the API: https links on the partner's domains, the
 * link template's placeholders, the disclosure); "Reset" goes back to the checked-in version. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Handshake, Pencil, Plus, Undo2 } from "lucide-react";
import { useState } from "react";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Button, Card, Field, Modal, PageHead, Spinner, TextInput, useToast } from "../../components/ui";
import { useT } from "../../i18n/I18nProvider";

type Partner = S["Partner"];

const TEMPLATE: S["PartnerWrite"] = {
  name: "New partner",
  kind: "finance",
  products: ["loan"],
  countries: ["US"],
  integration: "link",
  website: "https://example.com",
  signup_url: null,
  apply: { template: "{dealer_link}", dealer_field: "dealer_link", dealer_label: "Your application link", dealer_help: null, link_domains: ["example.com"], params: {} },
  order: null,
  disclosure: "{company} may receive a fee from {partner} if you apply for finance. {partner} decides on your application.",
  disclosure_required: false,
  disclosure_basis: null,
  referral_fee: null,
  api: { available: false, docs: null, contract_needed: true, adapter: null, env: [], notes: null },
  active: true,
};

function strip(p: Partner): S["PartnerWrite"] {
  const { key: _key, source: _source, ...rest } = p;
  return rest;
}

function Editor({ partnerKey, initial, isNew, onClose }: { partnerKey: string; initial: S["PartnerWrite"]; isNew: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const [key, setKey] = useState(partnerKey);
  const [text, setText] = useState(() => JSON.stringify(initial, null, 2));
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: S["PartnerWrite"]) => unwrap(api.PUT("/v1/admin/partners/{partner_key}", { params: { path: { partner_key: key } }, body })),
    onSuccess: (data) => {
      qc.setQueryData(["admin-partners"], data);
      qc.invalidateQueries({ queryKey: ["partners"] });
      toast(t("admin.partners.saved", { key }));
      onClose();
    },
    onError: (e) => setProblem(errorMessage(e)),
  });
  const submit = () => {
    try {
      setProblem(null);
      save.mutate(JSON.parse(text));
    } catch (e) {
      setProblem(t("admin.partners.invalidJson", { reason: (e as Error).message }));
    }
  };
  return (
    <Modal
      title={isNew ? t("admin.partners.addTitle") : t("admin.partners.editTitle", { key: partnerKey })}
      onClose={onClose}
      width={760}
      footer={
        <>
          <Button onClick={onClose}>{t("admin.common.cancel")}</Button>
          <Button variant="primary" loading={save.isPending} onClick={submit}>
            {t("admin.common.save")}
          </Button>
        </>
      }
    >
      <div className="stack">
        {isNew && (
          <Field label={t("admin.partners.key")} hint={t("admin.partners.keyHint")}>
            <TextInput value={key} dir="ltr" onChange={(e) => setKey(e.target.value)} />
          </Field>
        )}
        <Field label={t("admin.partners.entry")} hint={t("admin.partners.entryHint", { companyField: "{company}", partnerField: "{partner}", dealerId: "{dealer_id}", dealerLink: "{dealer_link}", feeField: "referral_fee" })}>
          <textarea className="input code" dir="ltr" aria-label={t("admin.partners.entry")} rows={22} value={text} spellCheck={false} onChange={(e) => setText(e.target.value)} style={{ fontFamily: "var(--mono, monospace)", fontSize: 12 }} />
        </Field>
        {problem && <Alert tone="danger">{problem}</Alert>}
      </div>
    </Modal>
  );
}

export function AdminPartners() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const catalog = useQuery({ queryKey: ["admin-partners"], queryFn: () => unwrap(api.GET("/v1/admin/partners")) });
  const [editing, setEditing] = useState<{ key: string; entry: S["PartnerWrite"]; isNew: boolean } | null>(null);
  const reset = useMutation({
    mutationFn: (key: string) => unwrap(api.DELETE("/v1/admin/partners/{partner_key}", { params: { path: { partner_key: key } } })),
    onSuccess: (data, key) => {
      qc.setQueryData(["admin-partners"], data);
      qc.invalidateQueries({ queryKey: ["partners"] });
      toast(t("admin.partners.reset", { key }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <>
      <PageHead
        title={t("admin.partners.title")}
        description={t("admin.partners.description")}
        actions={
          <Button icon={<Plus size={14} />} onClick={() => setEditing({ key: "", entry: TEMPLATE, isNew: true })}>
            {t("admin.partners.add")}
          </Button>
        }
      />
      <Card title={<span className="row" style={{ gap: 8 }}><Handshake size={16} /> {t("admin.partners.catalog")}</span>} pad={false}>
        {catalog.isLoading ? (
          <Spinner />
        ) : catalog.error || !catalog.data ? (
          <Alert tone="danger">{errorMessage(catalog.error)}</Alert>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t("admin.partners.partner")}</th>
                <th>{t("admin.partners.kind")}</th>
                <th>{t("admin.partners.countries")}</th>
                <th>{t("admin.partners.integration")}</th>
                <th>{t("admin.partners.fee")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {catalog.data.items.map((p) => (
                <tr key={p.key} className={p.active === false ? "muted" : undefined}>
                  <td>
                    <b dir="auto">{p.name}</b> <code className="faint" dir="ltr">{p.key}</code>
                    <div className="row" style={{ gap: 6, marginBlockStart: 2 }}>
                      {p.source !== "file" && <Badge tone="info">{p.source === "edited" ? t("admin.partners.edited") : t("admin.partners.added")}</Badge>}
                      {p.active === false && <Badge tone="warn">{t("admin.partners.inactive")}</Badge>}
                      {p.disclosure_required && <Badge plain>{t("admin.partners.disclosureRequired")}</Badge>}
                    </div>
                  </td>
                  <td>{t("admin.partners.kindName", { kind: p.kind })}</td>
                  <td dir="ltr">{p.countries.join(", ")}</td>
                  <td className="small">
                    {t("admin.partners.integrationValue", {
                      integration: p.integration === "api" ? "api" : "link",
                      api: !p.api.available ? "none" : p.api.contract_needed ? "contract" : "public",
                    })}
                  </td>
                  <td className="small muted" style={{ maxInlineSize: 280 }} dir="auto">{p.referral_fee ?? "—"}</td>
                  <td className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                    <Button size="sm" icon={<Pencil size={13} />} onClick={() => setEditing({ key: p.key, entry: strip(p), isNew: false })}>
                      {t("admin.common.edit")}
                    </Button>
                    {p.source !== "file" && (
                      <Button size="sm" variant="ghost" icon={<Undo2 size={13} />} loading={reset.isPending && reset.variables === p.key} onClick={() => reset.mutate(p.key)}>
                        {p.source === "added" ? t("admin.common.remove") : t("admin.partners.resetButton")}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {editing && <Editor partnerKey={editing.key} initial={editing.entry} isNew={editing.isNew} onClose={() => setEditing(null)} />}
    </>
  );
}
